import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {formEncodedApi} from 'nutshell-core/api';
import {getSingularEntityType} from 'nutshell-core/utils/get-singular-entity-type';

import type {SessionUser as SessionUserFragment} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../analytics-client';
import type {AnalyticsConstant} from '../../../analytics-client';
import type {NutshellState} from '../../../store';

import {
    closeComposer,
    promptOverrideMinimizedEmail,
    openComposerWithEmailAddress,
} from '../email-composer-actions';
import {
    getIsOpen,
    getSeededWithEmailAddress,
    getActiveEntityId,
    getShowEntityLink,
    getWasMinimizedForEntityId,
    getSeedWithAllPrimaryRelatedEmailAddresses,
} from '../email-composer-selectors';
import {EmailConfigureCtaModal} from '../email-configure-cta-modal';
import {EmailNoAddressesModal} from '../email-no-addresses-modal';
import {EmailOnboardingModal} from '../email-onboarding-modal';
import {MinimizedEmail} from '../minimized-email';
import {useGetEntityEmailAddresses} from '../use-get-entity-email-addresses';

import {EmailSenderModal} from './email-sender-modal';

type Select2EmailChoice = {
    entityId: string;
    id: string; // "acampos@aleris.com",
    address: string; // "acampos@aleris.com",,
    display: string; // "Autumn Campos",,
    text: string; // "Autumn Campos <acampos@aleris.com>"
};

type Props = {
    user: SessionUserFragment | null | undefined;
};

export function EmailSenderRoot(props: Props) {
    const [shouldShowOnboarding, setShouldShowOnboarding] = React.useState<boolean>(false);

    const isOpen = useSelector((state: NutshellState) => getIsOpen(state));
    const activeEntityId = useSelector((state: NutshellState) => getActiveEntityId(state));
    const showEntityLink = useSelector((state: NutshellState) => getShowEntityLink(state));
    const minimizedForEntityId = useSelector((state: NutshellState) =>
        getWasMinimizedForEntityId(state)
    );
    const seededWithEmailAddress = useSelector((state: NutshellState) =>
        getSeededWithEmailAddress(state)
    );
    const seedWithAllPrimaryRelatedEmailAddresses = useSelector((state: NutshellState) =>
        getSeedWithAllPrimaryRelatedEmailAddresses(state)
    );

    const dispatch = useDispatch();
    // @ts-expect-error
    const onExternalEmailAddressClicked = ({entityId, emailAddress}) => {
        dispatch(openComposerWithEmailAddress({entityId, emailAddress}));
    };
    const onClose = () => {
        dispatch(closeComposer());
    };
    const onPreventOpeningNewEmail = (entityId: string) => {
        dispatch(promptOverrideMinimizedEmail(entityId));
    };

    const handleMailToWasClicked = (unused: any, emailChoice: Select2EmailChoice) => {
        if (minimizedForEntityId && emailChoice.entityId !== minimizedForEntityId) {
            onPreventOpeningNewEmail(emailChoice.entityId);
        } else {
            onExternalEmailAddressClicked({
                entityId: emailChoice.entityId,
                emailAddress: emailChoice.id,
            });
        }
    };

    const handleOnboardingClose = () => {
        formEncodedApi.post('/my-account/save-user-setting.json', {
            setting: 'has_viewed_drip_modal_onboarding_NUT-10011',
            value: 1,
        });

        setShouldShowOnboarding(false);
    };

    React.useEffect(() => {
        $(window).on(Nut.View.events.MailToWasClicked, handleMailToWasClicked);

        return () => {
            $(window).off(Nut.View.events.MailToWasClicked, handleMailToWasClicked);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (isOpen) {
            const eventName = getPageType(window.location.pathname);
            analyticsClient.track(eventName, {
                fromMinimized: Boolean(minimizedForEntityId),
                location: window.location.pathname,
            });
        }
    }, [isOpen, minimizedForEntityId]);

    const {entity} = useGetEntityEmailAddresses(activeEntityId);

    if (!props.user) return null;

    if (!props.user.sendableEmailAddresses.length) {
        return <EmailConfigureCtaModal isOpen={isOpen} onClose={onClose} />;
    }

    if (shouldShowOnboarding) {
        return <EmailOnboardingModal isOpen={isOpen} onClose={handleOnboardingClose} />;
    }

    const getModal = () => {
        if (entity && entity.relatedEmailAddresses.all.length < 1) {
            return (
                <EmailNoAddressesModal
                    isOpen={isOpen}
                    onClose={onClose}
                    entityType={getSingularEntityType(entity.type)}
                    entityUrl={showEntityLink && entity.htmlUrl ? entity.htmlUrl : undefined}
                />
            );
        } else if (entity && props.user) {
            return (
                <EmailSenderModal
                    isOpen={isOpen}
                    user={props.user}
                    entity={entity}
                    seededWithEmailAddress={seededWithEmailAddress}
                    seedWithAllPrimaryRelatedEmailAddresses={
                        seedWithAllPrimaryRelatedEmailAddresses
                    }
                />
            );
        } else return undefined;
    };

    return (
        <>
            {getModal()}
            <MinimizedEmail />
        </>
    );
}

function getPageType(page: string): AnalyticsConstant {
    if (page.includes('person/') || page.includes('company/') || page.includes('lead/')) {
        return analyticsClient.EVENTS.TRACK_EMAIL_MODAL_OPENED_FROM_ENTITY;
    } else if (page.includes('dashboard')) {
        return analyticsClient.EVENTS.TRACK_EMAIL_MODAL_OPENED_FROM_DASHBOARD;
    } else if (page === '/people' || page === '/companies' || page === '/leads') {
        return analyticsClient.EVENTS.TRACK_EMAIL_MODAL_OPENED_FROM_LIST;
    } else {
        return analyticsClient.EVENTS.TRACK_EMAIL_MODAL_OPENED_FROM_UNKNOWN;
    }
}
